
import React, { useEffect, useRef } from 'react';
import "plyr/dist/plyr.css";

interface VideoPlayerProps {
  videoId: string;
  className?: string;
  videoTitle?: string;
  videoDescription?: string;
  uploadDate?: string;
  duration?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoId,
  className = '',
  videoTitle,
  videoDescription,
  uploadDate,
  duration
}) => {
  const playerRef = useRef<any>(null);

  useEffect(() => {
    let player: any = null;

    const initializePlayer = async () => {
      if (typeof window !== 'undefined') {
        const { default: Plyr } = await import('plyr');
        const element = document.querySelector(`[data-plyr-embed-id="${videoId}"]`);

        if (element) {
          const isLocalhost = window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';

          player = new Plyr(element as HTMLElement, {
            controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
            youtube: {
              noCookie: !isLocalhost,
              rel: 0,
              showinfo: 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              enablejsapi: 1,
              lazy: true,
              loading: 'lazy',
              origin: window.location.origin,
              widget_referrer: window.location.origin
            }
          });
          playerRef.current = player;
        }
      }
    };

    initializePlayer();

    return () => {
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
      }
    };
  }, [videoId]);

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": videoTitle || `Video ${videoId}`,
    "description": videoDescription || "",
    "thumbnailUrl": `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    "uploadDate": uploadDate || new Date().toISOString(),
    "duration": duration || "PT0M0S",
    "publisher": {
      "@type": "Organization",
      "name": "Bulletproof",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.bulletproof.co.uk/images/bp-structured-data-logo.png"
      }
    },
    "contentUrl": `https://www.youtube.com/watch?v=${videoId}`,
    "embedUrl": `https://www.youtube.com/embed/${videoId}`
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(videoSchema)}
      </script>
      <div className={`video video-responsive w-100 rounded-3 ${className}`}>
        <div
          data-plyr-provider="youtube"
          data-plyr-embed-id={videoId}
        ></div>
      </div>
    </>
  );
};

export default VideoPlayer;